import React, { useState, useEffect } from 'react'
import { Alert, Heading, Text, Stack, Box, Switch, Card, ImageLogo, Radio, RadioGroup, Checkbox, ColouredTag, Wrap, WrapItem } from '@/components/lib'
import api from '../../../api'
import useStore from '../../../store'
import utils from '../../../utils/utils'

import FitbitImage from '../../../images/fitbit.png'
import GarminImage from '../../../images/garmin.png'
import DexcomImage from '../../../images/dexcom.png'

export default function IntegrationsSettings ({ user, trial }) {
  const trialId = trial?._id
  const { updateTrial, team, groups } = useStore()
  const canManage = utils.canManageTrial(user, trial)
  const garminGroups = trial?.garminGroups || []
  // Whether the admin is choosing groups is local state: they pick the option
  // before any group is selected, and an empty `garminGroups` on the trial
  // means "everyone", so it can't be derived from the trial alone.
  const [garminScope, setGarminScope] = useState(garminGroups.length ? 'groups' : 'all')
  useEffect(() => {
    setGarminScope(trial?.garminGroups?.length ? 'groups' : 'all')
  }, [trialId])

  function toggleFitbitIntegration (e) {
    api.trials.update(trialId, { fitbitEnabled: e.checked }, newTrial => {
      updateTrial(newTrial._id, { fitbitEnabled: newTrial.fitbitEnabled ?? false })
    }, err => {
      utils.toast('error', 'Error updating Fitbit integration', err.message)
    })
  }

  function toggleGarminIntegration (e) {
    api.trials.update(trialId, { garminEnabled: e.checked }, newTrial => {
      updateTrial(newTrial._id, { garminEnabled: newTrial.garminEnabled ?? false })
    }, err => {
      utils.toast('error', 'Error updating Garmin integration', err.message)
    })
  }

  function saveGarminGroups (groupIds) {
    api.trials.update(trialId, { garminGroups: groupIds }, newTrial => {
      updateTrial(newTrial._id, { garminGroups: newTrial.garminGroups ?? [] })
    }, err => {
      utils.toast('error', 'Error updating Garmin groups', err.message)
    })
  }

  function setGarminScopeOption (scope) {
    setGarminScope(scope)
    // Switching back to the whole trial clears the restriction. Switching to
    // groups only takes effect once at least one group has been chosen.
    if (scope === 'all' && garminGroups.length) saveGarminGroups([])
  }

  function toggleGarminGroup (groupId) {
    const selected = garminGroups.some(id => String(id) === String(groupId))
    saveGarminGroups(selected
      ? garminGroups.filter(id => String(id) !== String(groupId))
      : [...garminGroups, groupId])
  }

  function toggleDexcomIntegration (e) {
    api.trials.update(trialId, { dexcomEnabled: e.checked }, newTrial => {
      updateTrial(newTrial._id, { dexcomEnabled: newTrial.dexcomEnabled ?? false })
    }, err => {
      utils.toast('error', 'Error updating Dexcom integration', err.message)
    })
  }

  return (
    <Stack gap={5}>
      <Box>
        <Heading size='md'>Wearables &amp; integrations</Heading>
        <Text>Configure integrations for this trial.</Text>
      </Box>

      {utils.hasFeature(team, 'fitbit') &&
        <Card variant='outline' gap={2}>
          <Box>
            <ImageLogo p={2} display='inline-block' src={FitbitImage} alt='Fitbit' w={100} />
          </Box>
          <Stack gap={2}>
            <Heading size='sm'>Fitbit</Heading>
            <Text>Allow Trialflare to connect to the Fitbit accounts of trial participants.</Text>
            {utils.hasFeature(team, 'fitbit')
              ? (
                <Stack alignItems='center' direction='row' spacing={4}>
                  <Switch
                    id='fitbit'
                    checked={trial?.fitbitEnabled}
                    disabled={!canManage}
                    onChange={toggleFitbitIntegration}
                  />
                  <Text>Enable Fitbit integration</Text>
                </Stack>)
              : (
                <Alert status='info'>
                  The Fitbit integration is not enabled for this team. Contact your account manager for more information.
                </Alert>)}
          </Stack>
        </Card>}

      <Card variant='outline' gap={2}>
        <Box>
          <ImageLogo p={2} display='inline-block' src={GarminImage} alt='Garmin' w={100} />
        </Box>
        <Stack gap={2}>
          <Heading size='sm'>Garmin</Heading>
          <Text>Allow Trialflare to connect to the Garmin accounts of trial participants.</Text>
          {utils.hasFeature(team, 'garmin')
            ? (
              <Stack alignItems='center' direction='row' spacing={4}>
                <Switch
                  id='garmin'
                  checked={trial?.garminEnabled}
                  disabled={!canManage}
                  onChange={toggleGarminIntegration}
                />
                <Text>Enable Garmin integration</Text>
              </Stack>)
            : (
              <Alert status='info'>
                The Garmin integration is not enabled for this team. Contact your account manager for more information.
              </Alert>)}

          {utils.hasFeature(team, 'garmin') && trial?.garminEnabled &&
            <Stack gap={3} borderTopWidth='1px' pt={3}>
              <Text fontWeight='bold'>Who is asked to connect Garmin?</Text>
              <RadioGroup
                value={garminScope}
                colorPalette='primary'
                onValueChange={e => setGarminScopeOption(e.value)}
              >
                <Stack gap={2}>
                  <Radio value='all' disabled={!canManage}>Everyone in this trial</Radio>
                  <Radio value='groups' disabled={!canManage || !groups?.length}>Only participants in selected groups</Radio>
                </Stack>
              </RadioGroup>

              {/* A restriction is set, so groups exist even if the store hasn't
                  loaded them yet — don't claim the trial has none. */}
              {!groups?.length && !garminGroups.length &&
                <Alert status='info'>
                  This trial doesn't have any participant groups yet. Create some to restrict the Garmin integration to a subset of participants.
                </Alert>}

              {garminScope === 'groups' && groups?.length > 0 &&
                <Stack gap={2}>
                  <Wrap gap={3}>
                    {groups.map(group =>
                      <WrapItem key={group._id}>
                        <Checkbox
                          id={`garmin-group-${group._id}`}
                          disabled={!canManage}
                          isChecked={garminGroups.some(id => String(id) === String(group._id))}
                          onChange={() => toggleGarminGroup(group._id)}
                        >
                          <ColouredTag size='sm' colour={group.colour}>{group.name}</ColouredTag>
                        </Checkbox>
                      </WrapItem>
                    )}
                  </Wrap>
                  {!garminGroups.length &&
                    <Alert status='warning'>
                      No groups are selected, so the Garmin connection is currently offered to every participant in the trial. Select at least one group to restrict it.
                    </Alert>}
                </Stack>}
            </Stack>}
        </Stack>
      </Card>

      {utils.hasFeature(team, 'dexcom') && (
        <Card variant='outline' gap={2}>
          <Box>
            <img src={DexcomImage} alt='Dexcom' width={100} />
          </Box>
          <Stack gap={2}>
            <Heading size='sm'>Dexcom</Heading>
            <Text>Allow Trialflare to connect to the Dexcom accounts of trial participants.</Text>
            {utils.hasFeature(team, 'dexcom')
              ? (
                <Stack alignItems='center' direction='row' spacing={4}>
                  <Switch
                    id='dexcom'
                    checked={trial?.dexcomEnabled}
                    disabled={!canManage}
                    onChange={toggleDexcomIntegration}
                  />
                  <Text>Enable Dexcom integration</Text>
                </Stack>)
              : (
                <Alert status='info'>
                  The Dexcom integration is not enabled for this team. Contact your account manager for more information.
                </Alert>)}
          </Stack>
        </Card>)}
    </Stack>
  )
}
